/* DeleteAccountSheet — soft-delete confirmation modal.
   Schedules the account for permanent deletion after a 7-day grace window.

   ⚠️ Security note: confirmation is the user's CURRENT PASSWORD (not a
   word like "حذف"). This defends against the common "unlocked phone"
   attack where a child, relative, or malicious party could otherwise
   wipe the account by typing a generic confirmation word. Same brute-
   force protection as /auth/login applies — 5 wrong attempts lock the
   account for 15 minutes.

   Backend route: DELETE /auth/me with body { password }.
   Restoration happens automatically on the next successful login during
   the grace period. */
import { useState } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import {
  X,
  Warning,
  Trash,
  ArrowCounterClockwise,
  Eye,
  EyeSlash,
  ShieldCheck,
} from "@phosphor-icons/react";
import { toast } from "sonner";
import { userDeleteMe, clearUserToken, formatUserApiError } from "../../lib/userApi";

export default function DeleteAccountSheet({ open, onClose }) {
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const nav = useNavigate();

  if (!open) return null;

  const canDelete = password.trim().length >= 1;

  const submit = async (e) => {
    e?.preventDefault?.();
    if (!canDelete || loading) return;
    setLoading(true);
    try {
      const res = await userDeleteMe(password);
      clearUserToken();
      const graceDays = res?.grace_days ?? 7;
      toast.success(
        `تَمَّت جَدوَلة حذف حسابك. لديك ${graceDays} أيّام لاستعادته بِتَسجيل الدخول.`,
        { duration: 7000 },
      );
      nav("/", { replace: true });
      // Hard reload to clear context state and force the login screen.
      setTimeout(() => window.location.reload(), 500);
    } catch (err) {
      toast.error(formatUserApiError(err));
      // Keep modal open + clear the password field so the user can retry,
      // but DON'T close — the next attempt may be the brute-force lockout.
      setPassword("");
      setLoading(false);
    }
  };

  return createPortal(
    <div className="fixed inset-0 z-[80] flex items-start justify-center pt-[env(safe-area-inset-top,0px)]" dir="rtl" data-testid="delete-account-sheet">
      <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={onClose} />
      <form
        onSubmit={submit}
        className="relative w-full max-w-md bg-white rounded-b-[28px] p-6 mt-0 font-tajawal shadow-2xl"
        style={{ animation: "lex-sheet-in 280ms cubic-bezier(0.22,1,0.36,1)" }}
      >
        <button
          type="button"
          onClick={onClose}
          className="absolute top-4 left-4 w-9 h-9 rounded-full bg-[#FAF9F6] flex items-center justify-center active:scale-95"
          aria-label="إغلاق"
          data-testid="delete-account-close"
        >
          <X size={18} weight="bold" />
        </button>

        <div className="flex items-center gap-3 mb-4">
          <div className="w-11 h-11 rounded-full bg-[#B91C1C]/12 flex items-center justify-center">
            <Warning size={22} weight="fill" color="#B91C1C" />
          </div>
          <div>
            <h2 className="text-[19px] font-bold text-[#B91C1C]">حذف الحساب</h2>
            <p className="text-[13px] text-[#5A6470]">قَرار حَسّاس — اقرأ التَّفاصيل أَدناه</p>
          </div>
        </div>

        {/* Grace-window explainer card */}
        <div className="bg-[#F0FDF4] border border-[#86EFAC] rounded-2xl p-4 mb-3 text-[13.5px] text-[#14532D] leading-relaxed">
          <div className="flex items-start gap-2">
            <ArrowCounterClockwise size={18} weight="bold" className="mt-0.5 shrink-0" />
            <div>
              <div className="font-bold mb-1">فَترة سَماح ٧ أيّام للاستِعادة</div>
              <p>
                لَن يُحذَف حسابك فَوراً. سَيبقى مُجَمَّداً لِمُدَّة <span className="font-bold">٧ أيّام</span>.
                إذا غَيَّرت رَأيك، يَكفي تَسجيل الدُّخول خِلال هذه الفَترة وسَيُستَعاد حسابك كَما كان.
              </p>
            </div>
          </div>
        </div>

        <div className="bg-[#FFF5F5] border border-[#FECACA] rounded-2xl p-4 mb-4 text-[13.5px] text-[#7F1D1D] leading-relaxed">
          <div className="font-bold mb-1">بَعد انقِضاء الـ ٧ أيّام:</div>
          سَتُحذَف نِهائيّاً ودون رَجعة:
          <ul className="mt-2 space-y-1 list-disc ms-5">
            <li>كافّة القضايا والتحليلات</li>
            <li>جميع الوثائق المسوّدة</li>
            <li>سجلّ العملاء والجدول الزمني</li>
            <li>محادثات الذكاء الاصطناعي والرسائل</li>
            <li>بِطاقة المُحامي الرَّقمية وإحصائيّات المُشاهَدات</li>
          </ul>
        </div>

        {/* Password confirmation — much safer than typing "حذف" */}
        <label className="block">
          <span className="text-[13px] text-[#0A192F] font-bold flex items-center gap-1.5">
            <ShieldCheck size={15} weight="fill" color="#B91C1C" />
            للتأكيد، أَدخِل كَلِمة مرور حسابك
          </span>
          <span className="block text-[11.5px] text-[#5A6470] mt-0.5 leading-relaxed">
            هذا يَمنَع أيّ شَخص آخَر يَستَخدِم جهازك من حذف حسابك دون إذنك.
          </span>
          <div className="relative mt-1.5">
            <input
              type={showPassword ? "text" : "password"}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              data-testid="delete-account-confirm-input"
              className="w-full pl-4 pr-12 py-3 rounded-2xl border border-[#EAEAEA] bg-[#FAF9F6] focus:outline-none focus:border-[#B91C1C] text-[15px] tracking-wide"
              placeholder="••••••••"
              autoComplete="current-password"
              autoFocus
              dir="ltr"
            />
            <button
              type="button"
              onClick={() => setShowPassword((s) => !s)}
              className="absolute top-1/2 right-3 -translate-y-1/2 w-8 h-8 flex items-center justify-center text-[#5A6470] hover:text-[#0A192F] active:scale-90"
              aria-label={showPassword ? "إخفاء كلمة المرور" : "إظهار كلمة المرور"}
              data-testid="delete-account-toggle-visibility"
            >
              {showPassword ? <EyeSlash size={18} weight="duotone" /> : <Eye size={18} weight="duotone" />}
            </button>
          </div>
        </label>

        <button
          type="submit"
          disabled={!canDelete || loading}
          data-testid="delete-account-submit"
          className="w-full mt-4 bg-[#B91C1C] text-white rounded-full py-3.5 text-[15px] font-bold flex items-center justify-center gap-2 active:scale-95 disabled:opacity-40 disabled:cursor-not-allowed"
        >
          <Trash size={16} weight="bold" />
          {loading ? "جارٍ التَّحقّق وَالجَدوَلة..." : "تَأكيد — جَدوِل حذف حسابي"}
        </button>

        <button
          type="button"
          onClick={onClose}
          className="w-full mt-2 text-[#5A6470] py-2 text-[14px]"
          data-testid="delete-account-cancel"
        >
          إلغاء — الاحتِفاظ بِحسابي
        </button>
      </form>
      <style>{`
        @keyframes lex-sheet-in {
          from { transform: translateY(20%); opacity: 0; }
          to   { transform: translateY(0); opacity: 1; }
        }
      `}</style>
    </div>,
    document.body
  );
}
